import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, Users, FolderKanban, Package, Code } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { WorkspaceOverviewService, OverviewTemplate } from '../services/workspaceOverviewService';

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  team: Users,
  project: FolderKanban,
  product: Package,
  code: Code,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Sem `maxTokens`/`temperature`: o `/overview/generate-content`, que atende a
   * geração de uma seção, usa os limites do backend. Os dois controles existiam
   * na tela e não iam para lugar nenhum — mostrar ajuste que não ajusta é pior
   * que não oferecer.
   */
  onGenerate: (prompt: string) => Promise<void>;
  isGenerating: boolean;
}

export const GenerateSectionDialog = ({ open, onOpenChange, onGenerate, isGenerating }: Props) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.startsWith('pt') ? 'pt' : 'en') as 'en' | 'pt';

  const [prompt, setPrompt] = useState('');
  const [templates, setTemplates] = useState<OverviewTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPrompt('');
    setSelectedTemplateKey(null);
    setIsLoadingTemplates(true);
    setTemplatesError(false);

    let cancelled = false;
    WorkspaceOverviewService.getBlockTemplates()
      .then(data => { if (!cancelled) setTemplates(data); })
      .catch(() => { if (!cancelled) setTemplatesError(true); })
      .finally(() => { if (!cancelled) setIsLoadingTemplates(false); });

    return () => { cancelled = true; };
  }, [open]);

  const handleSelectTemplate = (template: OverviewTemplate) => {
    if (selectedTemplateKey === template.key) {
      setSelectedTemplateKey(null);
      setPrompt('');
    } else {
      setSelectedTemplateKey(template.key);
      setPrompt(template.prompt[lang] || template.prompt.en);
    }
  };

  const handleGenerate = async () => {
    await onGenerate(prompt.trim());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[820px] max-h-[92vh] overflow-hidden flex flex-col items-stretch justify-start">
        <DialogHeader>
          <DialogTitle>{t('knowledge.overview.generate_section_title')}</DialogTitle>
        </DialogHeader>
        <div className="mt-0 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">{t('knowledge.overview.settings_ai_title')}</h3>
            <p className="text-xs text-muted-foreground mt-1">{t('knowledge.overview.settings_prompt_description')}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">{t('knowledge.overview.settings_templates_title')}</p>
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t('knowledge.overview.settings_templates_loading')}
              </div>
            ) : templatesError ? (
              <p className="text-xs text-muted-foreground/70 py-1">{t('knowledge.overview.settings_templates_error')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => {
                  const Icon = TEMPLATE_ICONS[tpl.key];
                  const isSelected = selectedTemplateKey === tpl.key;
                  return (
                    <Badge
                      key={tpl.key}
                      variant={isSelected ? 'default' : 'outline'}
                      className={`cursor-pointer select-none px-3 py-1.5 text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onClick={() => handleSelectTemplate(tpl)}
                    >
                      {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />}
                      {tpl.label[lang] || tpl.label.en}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          <div className="relative">
            <Textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setSelectedTemplateKey(null); }}
              placeholder={t('knowledge.overview.settings_prompt_placeholder')}
              className="h-[320px] font-mono text-xs resize-none"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-end">
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="bg-[#0B6FC2] hover:bg-[#0B6FC2]/90 text-white"
            >
              {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              {t('knowledge.overview.generate_confirm_action')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
